// Core lib dependencies.
import _ from 'lodash';

// Config.
import enums from '../config/enums.js';

/**
 * <pre>
 * Asset utility functions.
 *
 * Summary of responsibilities:
 *
 *  - provides commonly used functionality that can be called from anywhere.
 * </pre>
 *
 * @author Various
 * @namespace Common.Utils.Asset
 */

/**
 * Returns true if a given display item asset is a Vimeo video.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  item: {object} The display item.
 * @return {Boolean}
 */
function isVimeo(item) {
  let result = false;

  if (
    (item.content.link && item.content.link.indexOf('vimeo.com') > -1) ||
    item.meta.network === 'vimeo' ||
    item.assets.main.indexOf('vimeo') != -1
  ) {
    result = true;
  }

  return result;
}
export { isVimeo };

/**
 * Returns true if a given display item asset is a Youtube video.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  item: {object} The display item.
 * @return {Boolean}
 */
function isYoutube(item) {
  let result = false;

  if (
    item.embed.embedType == 'youtube' ||
    item.meta.network === 'youtube' ||
    item.assets.main.indexOf('youtube.') != -1 ||
    item.assets.main.indexOf('youtu.be') != -1
  ) {
    result = true;
  }

  return result;
}
export { isYoutube };

/**
 * Returns the id from a valid youtube video url.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  path: {string} The video url.
 * @return {string}
 */
function extractYoutubeId(path) {
  const patt = /(?:youtube\.com\/(?:[^\/]+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
  try {
    return patt.exec(path)[1];
  } catch (e) {
    return '';
  }
}
export { extractYoutubeId };

/**
 * Returns the thumbnail for a valid youtube video id.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  id: {string} The video id.
 * @param  res: {string} (standard, high) the desired resolution of the thumbnail image.
 * @return {string}
 */
function getYoutubeThumbnail(id, res = 'small') {
  if (res === 'small') {
    return 'https://img.youtube.com/vi/' + id + '/mqdefault.jpg';
  }
  if (res === 'medium') {
    return 'https://img.youtube.com/vi/' + id + '/hqdefault.jpg';
  }
}
export { getYoutubeThumbnail };

/**
 * Returns the thumbnail url for a valid Mux video playback id.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  asset: {object} The asset to use to generate the thumbnail
 * @return {string}
 */
function getMuxThumbnail(asset) {
  let src = null;

  if (asset.type === 'mux') {
    // Get the Mux playback id.
    const pathParts = asset.path.split(':');
    const playbackId = pathParts[0];
    const time = asset.meta.time || asset.meta.thumbnail || null;

    // If Mux asset is ready.
    if (asset.meta.status === enums.MUX_STATUS_READY) {
      src = getMuxThumbnailUrl(playbackId, time);
    }
  }
  return src;
}
export { getMuxThumbnail };

/**
 * Returns the thumbnail url for a valid Mux video playback id.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  id: {string} The video playback id.
 * @param  res: {string} The desired resolution of the thumbnail image.
 * @return {string}
 */
function getMuxThumbnailUrl(playbackId, time, res = '640') {
  let url = 'https://image.mux.com/' + playbackId + '/thumbnail.png?fit_mode=pad&width=' + res;
  if (time) {
    url += '&time=' + time;
  }
  return url;
}
export { getMuxThumbnailUrl };

/**
 * Attempts to retrieve a dynamic thumbnail from a given video items network.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  item: {object} The display item.
 * @return {Object}
 */
function getVideoAssetDynamicThumbnail(item) {
  const _assetThumb = {};
  // Youtube.
  if (isYoutube(item)) {
    if (item.assets.main) {
      const _videoId = extractYoutubeId(item.assets.main);
      _assetThumb.src = getYoutubeThumbnail(_videoId);
    }
  }

  // Mux.
  if (isStream(item)) {
    if (item.assets.main) {
      _assetThumb.src = getMuxThumbnail(assetFromRef(item.assets.main));
    }
  }

  // Handle other networks here if required...

  return _assetThumb;
}
export { getVideoAssetDynamicThumbnail };

/**
 * Returns true if a given display item asset is a Twitter video or gif.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  item: {object} The display item.
 * @return {Boolean}
 */
function isTwitterVideo(item) {
  let result = false;

  if (item.meta.network === 'twitter' && isVideoOrGif(item.meta.contentType)) {
    result = true;
  }

  return result;
}
export { isTwitterVideo };

/**
 * Returns true if a given display item asset is a Instagram video.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  item: {object} The display item.
 * @return {Boolean}
 */
function isInstagram(item) {
  let result = false;

  if (
    (item.content.link && item.content.link.indexOf('instagram.com') > -1) ||
    item.meta.network === 'instagram' ||
    item.assets.main.indexOf('instagram') != -1
  ) {
    result = true;
  }

  return result;
}
export { isInstagram };

/**
 * Returns true if a given display item asset is a Vine asset.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  item: {object} The display item.
 * @return {Boolean}
 */
function isVine(item) {
  let result = false;

  if (
    (item.content.link && item.content.link.indexOf('vine.co') > -1) ||
    item.meta.network === 'vine' ||
    item.assets.main.indexOf('vine') != -1
  ) {
    result = true;
  }

  return result;
}
export { isVine };

/**
 * Returns true if a given display item asset is a Facebook video.
 * @param  item: {object} The display item.
 * @return {Boolean}
 */
export function isFacebook(item) {
  let result = false;

  if (item.meta.network === 'facebook' && item.assets.main.indexOf('.fbcdn') != -1) {
    result = true;
  }

  return result;
}

/**
 * Returns true if a given display item asset is a Stream video.
 * @param  item: {object} The display item.
 * @return {Boolean}
 */
export function isStream(item) {
  let result = false;

  if (isVideoOrGif(item.meta.contentType) && item.assets.main.indexOf('mux') != -1) {
    result = true;
  }

  return result;
}

/**
 * Returns string name of video type.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  item: {object} The display item.
 * @return {String}
 */
function getAssetVideoType(item) {
  let result = '';

  if (isVideoOrGif(_.get(item, ['meta', 'contentType']))) {
    if (isVimeo(item)) {
      result = 'Vimeo';
    } else if (isYoutube(item)) {
      result = 'Youtube';
    } else if (isTwitterVideo(item)) {
      result = 'Twitter';
    } else if (isInstagram(item)) {
      result = 'Instagram';
    } else if (isVine(item)) {
      result = 'Vine';
    } else if (isFacebook(item)) {
      result = 'Facebook';
    } else if (isStream(item)) {
      result = 'Stream';
    } else {
      result = 'GenericVideo';
    }
  }

  return result;
}
export { getAssetVideoType };

/**
 * Returns string safe for React to render
 *
 * @memberOf Common.Utils.Asset
 *
 * @param assetRef : {string} valid NGX asset ref
 * @returns {object} with HTML sanitized for React
 */
function assetFromRef(assetRef, assetUrl, assetUgcUrl) {
  if (typeof assetRef === 'string' && assetRef.indexOf(':') > -1) {
    var assetRefParts = assetRef.split(':');
    // Some asset refs have an additional colon for extra information. Ex ":large"
    // This is to maintain that additional information
    if (assetRefParts.length > 4) assetRefParts[3] = _.drop(assetRefParts, 3).join(':');

    var src;
    if (assetRefParts[0] == 'asset' || assetRefParts[0] == 'bundle') {
      src = `${assetUrl}${assetRefParts[3]}`;
    } else if (assetRefParts[0] == 'ugc') {
      src = `${assetUgcUrl}${assetRefParts[3]}`;
    } else {
      src = assetRefParts[3] || '';
    }

    // If meta data exists, parse it.
    let meta = {};
    if (assetRefParts[4]) {
      try {
        meta = JSON.parse(
          '{"' +
            decodeURI(assetRefParts[4])
              .replace(/"/g, '\\"')
              .replace(/&/g, '","')
              .replace(/=/g, '":"') +
            '"}'
        );
      } catch (e) {
        //console.log('Could not parse asset ref options:' + assetRefParts[4]);
        //console.log(assetRefParts);
      }
    }

    return {
      type:      assetRefParts[0],
      assetType: assetRefParts[1],
      size:      (function(s) {
        var sArr = s.split('x');
        return {
          width:  sArr[0] || 0,
          height: sArr[1] || 0,
        };
      })(assetRefParts[2] || ''),
      path: assetRefParts[3],
      src:  src,
      meta: meta,
    };
  }
  return false;
}
export { assetFromRef };

/**
 * Gets a list of classes for an asset
 *
 * @memberOf Common.Utils.Asset
 *
 * @param contentType : {string}
 * @returns {array} of classnames to be used or .join(' ')ed
 */
function getAssetClasses(contentType) {
  const _wrapperClasses = ['xMediaContainer'];

  if (contentType) {
    if (isVideoOrGif(contentType)) {
      _wrapperClasses.push('xPlayable', 'xMediaVideo');
    } else if (contentType == 'image') {
      _wrapperClasses.push('xMediaImage');
    }
  }

  return _wrapperClasses;
}
export { getAssetClasses };

/**
 * Formats asset thumbnail url for given social network and content type.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param thumbnailUrl : {string}
 * @param network : {string}
 * @param contentType : {string}
 * @returns {string}
 */
function formatThumbnailAsset(thumbnailUrl, network, contentType, res = 'small') {
  let formattedThumbnailUrl = thumbnailUrl;
  switch (network) {
    case 'twitter':
      if (contentType === 'video' && !!extractYoutubeId(thumbnailUrl)) {
        const _videoId = extractYoutubeId(thumbnailUrl);
        formattedThumbnailUrl = getYoutubeThumbnail(_videoId, res);
      }
      break;
    case 'instagram':
      if (contentType === 'video') {
        const targetSize = 'size=l';
        formattedThumbnailUrl = formattedThumbnailUrl.replace('size=t', targetSize);
      }
      break;
  }

  return formattedThumbnailUrl;
}
export { formatThumbnailAsset };

function getDisplayAssets(item, assetUrl, assetUgcUrl, res) {
  const contentType = _.get(item, 'meta.contentType');
  const assetClasses = getAssetClasses(contentType).join(' ');
  const mainAsset = _.get(item, 'assets.main');
  const network = _.get(item, 'meta.network');

  let thumbnail;
  // If we have an asset.
  if (item.assets) {
    // If asset is a video.
    if (isVideoOrGif(contentType)) {
      // Attempt to retrieve the stored thumbnail for this video asset.
      if (!_.isEmpty(item.assets.sizes) && !!item.assets.sizes[res]) {
        // Doing this here to get the type of the asset
        const asset = assetFromRef(item.assets.sizes[res], assetUrl, assetUgcUrl);
        if (!!asset && asset.assetType === 'image') {
          thumbnail = asset;
        }
      }

      // Thumbnail will be undefined if any of the above logic fails
      if (!thumbnail) {
        thumbnail = assetFromRef(item.assets.thumbnail, assetUrl, assetUgcUrl);
      }

      // Mux.
      if (thumbnail.type === 'mux') {
        thumbnail.src = getMuxThumbnail(thumbnail);
      }

      // If for some reason we have received a video asset with no thumbnail,
      // then attempt to dynamically build path to the video networks own thumbnail image.
      if (!thumbnail && !thumbnail.src) {
        thumbnail = getVideoAssetDynamicThumbnail(item);
      }
      // Otherwise if NOT a video.
    } else {
      thumbnail = assetFromRef(mainAsset || _.get(item, 'content.featureImageAssetRef'), assetUrl, assetUgcUrl);
    }

    // If no assets, but there is a featured image ref.
  } else if (_.get(item, 'content.featureImageAssetRef')) {
    thumbnail = assetFromRef(_.get(item, 'content.featureImageAssetRef'), assetUrl, assetUgcUrl);
  }
  if (thumbnail) {
    thumbnail.formattedThumbnailAsset = formatThumbnailAsset(_.get(thumbnail, ['src']), network, contentType, res);
  }

  return {
    thumbnail,
    assetClasses,
  };
}

export { getDisplayAssets };

/**
 * Returns true if a given display item's contentType is video or gif.
 *
 * @memberOf Common.Utils.Asset
 *
 * @param  contentType: {string} The display item's contentType.
 * @return {Boolean}
 */
export function isVideoOrGif(contentType) {
  return contentType === 'video' || contentType === 'gif';
}

function getThumbnail(displayAssets, size = 'small') {
  return _.get(displayAssets, ['thumbnail', 'formattedThumbnailAsset']);
}

export { getThumbnail };
